import * as React from "react";
import { cn } from "../utils";

export interface DialogChildrenPartition {
  readonly bodyChildren: readonly React.ReactNode[];
  readonly footerChildren: readonly React.ReactNode[];
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 flex-col-reverse overflow-visible sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}
DialogFooter.displayName = "DialogFooter";

export function isDialogFooterElement(node: React.ReactNode): boolean {
  return React.isValidElement(node) && node.type === DialogFooter;
}

function isReactFragmentElement(
  node: React.ReactNode,
): node is React.ReactElement<{ children?: React.ReactNode }> {
  return React.isValidElement(node) && node.type === React.Fragment;
}

function collectDialogChildren(
  node: React.ReactNode,
  bodyChildren: React.ReactNode[],
  footerChildren: React.ReactNode[],
): void {
  const items: readonly React.ReactNode[] = React.Children.toArray(node);
  for (const child of items) {
    if (isReactFragmentElement(child)) {
      collectDialogChildren(child.props.children, bodyChildren, footerChildren);
      continue;
    }
    if (isDialogFooterElement(child)) {
      footerChildren.push(child);
      continue;
    }
    bodyChildren.push(child);
  }
}

/** Split `DialogFooter` out of the scroll body so focus rings are not clipped. */
export function partitionDialogChildren(
  children: React.ReactNode,
): DialogChildrenPartition {
  const bodyChildren: React.ReactNode[] = [];
  const footerChildren: React.ReactNode[] = [];
  collectDialogChildren(children, bodyChildren, footerChildren);
  return { bodyChildren, footerChildren };
}
